import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Download, FileText, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Informes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tipoInforme, setTipoInforme] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [trabajos, setTrabajos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.rol === 'administrador') {
      cargarUsuarios();
    }
  }, []);

  const cargarUsuarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const [clientesRes, tecnicosRes] = await Promise.all([
        axios.get(`${API}/admin/clientes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/tecnicos`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setClientes(clientesRes.data);
      setTecnicos(tecnicosRes.data);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  const generarInforme = async () => {
    if (!tipoInforme) {
      setError('Selecciona un tipo de informe');
      return;
    }

    setError('');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (tipoInforme === 'por-cliente') {
        endpoint = filtro !== 'todos' 
          ? `${API}/informes/trabajos-por-cliente?cliente_id=${filtro}`
          : `${API}/informes/trabajos-por-cliente`;
      } else if (tipoInforme === 'por-tecnico') {
        endpoint = filtro !== 'todos'
          ? `${API}/informes/trabajos-por-tecnico?tecnico_id=${filtro}`
          : `${API}/informes/trabajos-por-tecnico`;
      } else if (tipoInforme === 'mis-trabajos') {
        endpoint = `${API}/informes/mis-trabajos-tecnico`;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrabajos(response.data);
    } catch (err) {
      setError('Error al generar el informe');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (trabajos.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const data = trabajos.map(t => ({
      'Folio': t.folio || 'N/A',
      'Título': t.titulo,
      'Descripción': t.descripcion,
      'Cliente': t.cliente_nombre || 'N/A',
      'Técnico': t.tecnico_asignado_nombre || 'N/A',
      'Estado': t.estado,
      'Fecha Creación': new Date(t.fecha_creacion).toLocaleDateString('es-ES'),
      'Fecha Finalización': t.fecha_finalizacion ? new Date(t.fecha_finalizacion).toLocaleDateString('es-ES') : 'N/A',
      'Horas Trabajadas': t.horas_trabajadas || 'N/A',
      'Dirección': t.direccion || 'N/A',
      'Materiales': t.materiales_usados || 'N/A',
      'Observaciones': t.observaciones || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Informe');
    
    const filename = `informe_${tipoInforme}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const exportarPDF = async () => {
    if (trabajos.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Por ahora mostrar alert, implementación completa requiere librería PDF
    alert('Función de exportar PDF disponible. Se puede implementar con jsPDF o similar.');
  };

  const formatEstado = (estado) => {
    const estados = {
      'pendiente': 'Pendiente',
      'en_progreso': 'En Progreso',
      'completado': 'Completado'
    };
    return estados[estado] || estado;
  };

  const getEstadoColor = (estado) => {
    const colores = {
      'pendiente': 'bg-yellow-100 text-yellow-800',
      'en_progreso': 'bg-blue-100 text-blue-800',
      'completado': 'bg-green-100 text-green-800'
    };
    return colores[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Generador de Informes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Tipo de Informe */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de Informe</label>
                <Select value={tipoInforme} onValueChange={setTipoInforme}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {user?.rol === 'administrador' && (
                      <>
                        <SelectItem value="por-cliente">Trabajos por Cliente</SelectItem>
                        <SelectItem value="por-tecnico">Trabajos por Técnico</SelectItem>
                      </>
                    )}
                    {user?.rol === 'tecnico' && (
                      <SelectItem value="mis-trabajos">Mis Trabajos</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro (solo para admin) */}
              {user?.rol === 'administrador' && tipoInforme && tipoInforme !== 'mis-trabajos' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {tipoInforme === 'por-cliente' ? 'Cliente' : 'Técnico'}
                  </label>
                  <Select value={filtro} onValueChange={setFiltro}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {tipoInforme === 'por-cliente' && clientes.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nombre_completo}
                        </SelectItem>
                      ))}
                      {tipoInforme === 'por-tecnico' && tecnicos.map(tecnico => (
                        <SelectItem key={tecnico.id} value={tecnico.id}>
                          {tecnico.nombre_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Botón Generar */}
              <div className="space-y-2">
                <label className="text-sm font-medium">&nbsp;</label>
                <Button
                  onClick={generarInforme}
                  disabled={loading || !tipoInforme}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Generar Informe
                    </>
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Resultados */}
        {trabajos.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle>
                  Resultados ({trabajos.length} trabajo{trabajos.length !== 1 ? 's' : ''})
                </CardTitle>
                <div className="flex gap-2">
                  <Button onClick={exportarExcel} variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Excel
                  </Button>
                  <Button onClick={exportarPDF} variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Folio</th>
                      <th className="text-left p-3 font-semibold">Trabajo</th>
                      <th className="text-left p-3 font-semibold">Cliente</th>
                      <th className="text-left p-3 font-semibold">Técnico</th>
                      <th className="text-left p-3 font-semibold">Estado</th>
                      <th className="text-left p-3 font-semibold">Fecha Creación</th>
                      <th className="text-left p-3 font-semibold">Horas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trabajos.map((trabajo) => (
                      <tr
                        key={trabajo.id}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(user?.rol === 'tecnico' ? `/tecnico/trabajo/${trabajo.id}` : `/admin/trabajo/${trabajo.id}`)}
                      >
                        <td className="p-3 font-mono text-sm">{trabajo.folio || 'N/A'}</td>
                        <td className="p-3">
                          <div className="font-medium">{trabajo.titulo}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{trabajo.descripcion}</div>
                        </td>
                        <td className="p-3 text-sm">{trabajo.cliente_nombre || 'N/A'}</td>
                        <td className="p-3 text-sm">{trabajo.tecnico_asignado_nombre || 'N/A'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(trabajo.estado)}`}>
                            {formatEstado(trabajo.estado)}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {new Date(trabajo.fecha_creacion).toLocaleDateString('es-ES')}
                        </td>
                        <td className="p-3 text-sm">{trabajo.horas_trabajadas || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && trabajos.length === 0 && tipoInforme && (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron trabajos para los criterios seleccionados</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Informes;
